import { createFileRoute, Link } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { useEffect, useMemo } from 'react'
import { MdCheckCircle, MdInfo, MdUnpublished } from 'react-icons/md'
import { useAssistant, useDataSet, useDataSources, useModules, useUpdateAssistant } from '~/api'
import { AppHeading } from '~/components/educator/app/AppHeading'
import { AppModuleRow } from '~/components/educator/data/AppModuleRow'
import { Toggle } from '~/components/shared/Toggle'
import { Assistant } from '~/model'
import { appSelectedResourcesAtom } from '~/state'
import { Button, Checkbox, cn, InfoTooltip, Label } from '~ui'

export const Route = createFileRoute('/educator/courses/$courseId/apps/$appId/sources')({
	component: Sources,
})

function Sources() {
	const { data: modules } = useModules()
	const { appId } = Route.useParams()
	const app = useAssistant(appId)
	const { mutate: updateAssistant, isPending } = useUpdateAssistant()
	const { dataSet } = useDataSet()
	const { dataSourcesById } = useDataSources()
	const [selectedResources, setSelectedResources] = useAtom(appSelectedResourcesAtom)

	useEffect(() => {
		if (!app || !modules.length) return

		if (app.useEntireDataset) {
			selectAll()
		} else {
			// hydrate from app.data-sources
			const next: Record<string, boolean> = {}
			modules.forEach((module) => {
				module.items.forEach((item) => {
					const isReady = dataSourcesById?.[item.id]?.status === 'READY'
					const isAttached = app.datasources?.some((ds) => ds.uniqueTag === dataSourcesById?.[item.id]?.unique_tag)
					next[item.id] = isReady && !!isAttached

					item.children?.forEach((child) => {
						const isChildReady = dataSourcesById?.[child.id]?.status === 'READY'
						const isChildAttached = app.datasources?.some((ds) => ds.uniqueTag === dataSourcesById?.[child.id]?.unique_tag)
						next[child.id] = isChildReady && !!isChildAttached
					})
				})
			})
			setSelectedResources(next)
		}
	}, [app, modules, dataSourcesById, setSelectedResources])

	function selectAll() {
		const next: Record<string, boolean> = {}
		modules.forEach((module) => {
			module.items.forEach((item) => {
				next[item.id] = dataSourcesById?.[item.id]?.status === 'READY'
				item.children?.forEach((child) => {
					next[child.id] = dataSourcesById?.[child.id]?.status === 'READY'
				})
			})
		})
		setSelectedResources(next)
	}

	const allReadyItems = useMemo(() => {
		return modules.reduce<string[]>((acc, module) => {
			module.items.forEach((item) => {
				if (dataSourcesById?.[item.id]?.status === 'READY') {
					acc.push(item.id)
				}
				item.children?.forEach((child) => {
					if (dataSourcesById?.[child.id]?.status === 'READY') {
						acc.push(child.id)
					}
				})
			})
			return acc
		}, [])
	}, [modules, dataSourcesById])

	const allSelected = useMemo(() => {
		return allReadyItems.every((id) => selectedResources[id])
	}, [selectedResources, allReadyItems])

	const noResourcesSelected = useMemo(() => {
		return Object.values(selectedResources).every((enabled) => !enabled)
	}, [selectedResources])

	const isRag = app?.assistantType === 'RAG'
	const disabled = dataSet?.status !== 'READY' || !isRag || isPending

	const selectionChanged = useMemo(() => {
		if (!app) return false

		// Case A: saved is entire dataset
		// if useEntireDataset was true, it's a change when NOT allSelected.
		if (app.useEntireDataset) {
			return !allSelected
		}

		// Case B: saved is partial, but UI now has ALL selected.
		// We want to normalize to the canonical "useEntireDataset: true, datasources: []".
		if (allSelected) {
			return true
		}

		// Otherwise, do a set diff between saved uniqueTags and current selection.
		const savedTags = new Set(app.datasources?.map(ds => ds.uniqueTag))
		const currentlySelected = Object.entries(selectedResources)
			.filter(([, enabled]) => enabled)
			.map(([id]) => dataSourcesById?.[id]?.unique_tag)
			.filter(Boolean) as string[]

		if (currentlySelected.length !== savedTags.size) return true
		return currentlySelected.some(tag => !savedTags.has(tag))
	}, [app, allSelected, selectedResources, dataSourcesById])

	function updateResources() {
		if (!app) return

		const newApp = {
			...app,
			useEntireDataset: allSelected,
			datasources: allSelected
				? []
				: (Object.entries(selectedResources)
						.map(([id, enabled]) => {
							if (!enabled) return undefined
							const dataSource = dataSourcesById?.[id]
							if (!dataSource) return undefined
							return {
								id: dataSource.id,
								uniqueTag: dataSource.unique_tag,
							}
						})
						.filter(Boolean) as Assistant['datasources']),
		}
		updateAssistant(newApp)
	}

	return (
		<>
			<AppHeading>
				<Button
					disabled={disabled || noResourcesSelected || !selectionChanged}
					onClick={updateResources}
				>
					Update resources
				</Button>
			</AppHeading>

			<div className="max-w-text flex items-end justify-between">
				<h1 className="mb-[4px]">Information sources</h1>
				<InfoTooltip>
					<p>Decide whether the app uses LMS resources from the subject’s dataset or the AI’s general knowledge.</p>
					<p className="mt-[12px]">When LMS resources are enabled, Aila is designed to generate answers using only the content selected below.</p>
					<p className="mt-[12px]">When LMS resources are disabled, Aila will rely solely on the AI’s pre-trained general knowledge and will not reference the subject’s dataset</p>
					<p className="mt-[12px]">This setting directly affects how Aila interprets questions and constructs responses.</p>
				</InfoTooltip>
			</div>
			<p className="max-w-text mb-[24px]">Select how this app generates answers for students. You can use LMS resources or the AI’s general knowledge.</p>

			<section
				aria-labelledby="show-learning-assistant-in-lms"
				className="border-dark-grey-25 max-w-text mt-[12px] mb-[24px] flex justify-between gap-x-[24px] border p-[24px]"
			>
				<div>
					<h3
						id="show-learning-assistant-in-lms"
						className="text-navy-100 text-[20px] leading-[140%] font-[600]"
					>
						LMS resources
					</h3>
					<p className="mt-[4px]">Toggle on to use LMS resources. Toggle off to rely solely on the AI’s general knowledge.</p>
					<div className="flex items-center gap-x-[12px]">
						<div className={cn(isRag ? 'bg-light-blue-25' : 'bg-light-grey-25', 'text-navy-100 mt-[12px] flex max-w-max items-center gap-x-[4px] rounded-[2px] px-[8px] py-[4px]')}>
							{isRag ? <MdCheckCircle className="size-[16px]" /> : <MdUnpublished className="size-[16px]" />}
							<div>App is {!isRag && 'not'} using LMS resources</div>
						</div>
					</div>
				</div>
				<div className="flex items-center justify-center">
					<Toggle
						checked={isRag}
						disabled={app?.isTemplate}
						onChange={(checked) => {
							if (!app) return
							updateAssistant({
								...app,
								assistantType: checked ? 'RAG' : 'CHAT',
								useEntireDataset: true,
								datasources: [],
							})
						}}
						screenReaderLabel="Show learning assistant in LMS"
					/>
				</div>
			</section>

			{!app?.isTemplate && allReadyItems.length > 0 && (
				<>
					<h2 className="text-navy-100 font-600 mb-[4px] text-[20px]">LMS resources</h2>
					<p className="max-w-text mb-[24px]">
						All apps in this subject use the Subject dataset. Use this screen to choose which resources this app can reference — it won’t affect what other apps can see or use.
					</p>

					<div className={cn(disabled && 'pointer-events-none opacity-30')}>
						<div className="mt-[24px] flex items-center gap-x-[8px]">
							<Checkbox
								id="select-all"
								checked={allSelected}
								onCheckedChange={(checked) => {
									if (checked === true) {
										setSelectedResources(
											modules.reduce<Record<string, boolean>>((acc, module) => {
												module.items.forEach((item) => {
													acc[item.id] = true
													item.children?.forEach((child) => {
														acc[child.id] = true
													})
												})
												return acc
											}, {}),
										)
									} else {
										setSelectedResources({})
									}
								}}
							/>
							<Label
								htmlFor="select-all"
								className="font-700 mb-0 p-0 text-center text-[14px]"
							>
								Select all
							</Label>
						</div>
						<section
							className="mt-[24px] flex flex-col gap-y-[12px]"
							aria-label="LMS Modules"
						>
							{modules.map((module) => (
								<AppModuleRow
									key={module.id}
									module={module}
								/>
							))}
						</section>
					</div>
				</>
			)}

			{dataSet != null && !app?.isTemplate && (dataSet.status !== 'READY' || !allReadyItems.length) && (
				<div
					className="max-w-text bg-light-blue-25 text-navy-100 mb-[24px] flex items-center gap-[10px] p-[12px]"
					role="alert"
				>
					<div className="bg-light-blue-50 flex size-[32px] shrink-0 items-center justify-center">
						<MdInfo
							className="size-[20px]"
							aria-hidden
						/>
						<div className="sr-only">Info</div>
					</div>
					<div>
						You don’t have a subject dataset. You need to create a{' '}
						<Link
							className="underline"
							from="/educator/courses/$courseId"
							to="/educator/courses/$courseId/data"
						>
							subject dataset
						</Link>{' '}
						before you can select resources within this app.
					</div>
				</div>
			)}
		</>
	)
}
